import React from 'react';
import { useImmer } from 'use-immer';
import { toFlag, toPercent } from 'pano-utils';
import { useGate } from 'effector-react';
import pick from 'ramda/es/pick';
import {
  type Subtitle,
  SubtitleClickAction,
  SubtitleRollingMode,
} from '@types';
import Title from '@components/Title';
import { Collapse } from '@components/Collapse';
import Textarea from '@components/Textarea';
import useTextareaHandelrs from '@hooks/useTextareaHandelrs';
import IconReset from '@components/icons/IconReset';
import ColorPicker from '@components/ColorPicker';
import Slider from '@components/Slider';
import { Dropdown, type DropdownItem } from '@components/Dropdown';
import { Validator } from '@components/Validator';
import { $GateEditingSubTitle } from '@stores/editingStores';
import Tip from '@components/Tip';
import PanelBase from '../../panel/PanelBase';
import { SUBTITLE_DEFAULT } from '../constants';
import LinkSetting from '../../panel/components/LinkSetting';

export interface PanelSubtitleProps {
  defaultValue?: Subtitle;
  index: number;
  onConfirm: (index: number, subtitle: Subtitle) => void;
  onDeleteClick: (index: number) => void;
  onRequestClose: () => void;
}

const fontItems: DropdownItem[] = [
  { id: 12, name: '12px' },
  { id: 14, name: '14px' },
  { id: 16, name: '16px' },
];

const modeItems: DropdownItem<SubtitleRollingMode>[] = [
  { id: SubtitleRollingMode.left, name: '向左滚动' },
  { id: SubtitleRollingMode.static, name: '静止显示' },
];

const actionsItems: DropdownItem<SubtitleClickAction>[] = [
  { id: SubtitleClickAction.none, name: '无操作' },
  { id: SubtitleClickAction.link, name: '打开超链接' },
];

const settingPick = pick([
  'fontSize',
  'fontColor',
  'bgColor',
  'spacing',
  'rollingMode',
  'rollingSpeed',
  'pauseDuration',
]);

const settings = {
  current: SUBTITLE_DEFAULT,
};

export function PanelSubtitle({
  defaultValue = { ...SUBTITLE_DEFAULT, ...settingPick(settings.current) },
  index,
  onConfirm,
  onRequestClose,
  onDeleteClick,
}: PanelSubtitleProps) {
  const [state, setState] = useImmer(defaultValue);
  const isNew = !defaultValue;
  useGate($GateEditingSubTitle, state);

  const textareaHandler = useTextareaHandelrs('trimRight', (val) => {
    setState((draft) => {
      draft.text = val;
    });
  });

  const renderContent = () => {
    return (
      <Collapse title="字幕内容">
        <Validator
          value={state.text}
          validFn={(value) => !!value}
          warningMessage="请输入字幕内容"
        >
          {({ value, warning }) => (
            <Textarea
              scheme="dark"
              className="h-115"
              placeholder="请输入字幕内容"
              maxLength={1000}
              value={value}
              warning={warning}
              {...textareaHandler}
            />
          )}
        </Validator>
      </Collapse>
    );
  };

  const renderSubTitleSetting = () => {
    return (
      <Collapse title="字幕样式">
        <div className="flex items-center justify-between">
          <div>字号大小</div>
          <IconReset
            onClick={() => {
              setState((draft) => {
                draft.fontSize = SUBTITLE_DEFAULT.fontSize;
              });
            }}
          />
        </div>
        <Dropdown
          className="mt-10"
          items={fontItems}
          selected={state.fontSize}
          onChange={(val) => {
            setState((draft) => {
              draft.fontSize = val;
            });
          }}
        />
        <ColorPicker
          className="mt-20"
          colorString={state.fontColor}
          onChange={(str) =>
            setState((draft) => {
              draft.fontColor = str;
            })
          }
        >
          <ColorPicker.Header>
            <div>字体颜色：</div>
            <ColorPicker.Reset defaultColor={SUBTITLE_DEFAULT.fontColor!} />
          </ColorPicker.Header>
          <ColorPicker.Bar />
        </ColorPicker>
        <ColorPicker
          className="mt-20"
          colorString={state.bgColor}
          onChange={(str) =>
            setState((draft) => {
              draft.bgColor = str;
            })
          }
        >
          <ColorPicker.Header>
            <div>背景颜色：</div>
            <ColorPicker.Reset defaultColor={SUBTITLE_DEFAULT.bgColor!} />
          </ColorPicker.Header>
          <ColorPicker.Bar />
        </ColorPicker>
        <Slider
          className="mt-20"
          value={state.spacing}
          min={0}
          max={50}
          valToString={(val) => `${val}px`}
          onChange={(val) => {
            setState((draft) => {
              draft.spacing = val;
            });
          }}
        >
          <Slider.Header title="字体间距：" />
          <Slider.Bar />
          <Slider.Control />
        </Slider>
      </Collapse>
    );
  };

  const renderPlaySetting = () => {
    return (
      <Collapse title="播放交互">
        <Dropdown
          items={modeItems}
          selected={state.rollingMode}
          onChange={(typeId) => {
            setState((draft) => {
              draft.rollingMode = typeId;
              draft.rollingSpeed = SUBTITLE_DEFAULT.rollingSpeed;
              if (typeId === SubtitleRollingMode.static) {
                draft.pauseDuration = 5;
              } else {
                draft.pauseDuration = SUBTITLE_DEFAULT.pauseDuration;
              }
            });
          }}
        />
        {state.rollingMode === SubtitleRollingMode.left && (
          <Slider
            className="mt-20"
            value={state.rollingSpeed}
            min={0.5}
            max={2}
            valToString={(val) => toPercent(val, 0)}
            onChange={(val) => {
              setState((draft) => {
                draft.rollingSpeed = val;
              });
            }}
          >
            <Slider.Header title="滚动速度" />
            <Slider.Bar step={0.01} />
            <Slider.Control step={0.01} />
          </Slider>
        )}
        {state.rollingMode === SubtitleRollingMode.static && (
          <>
            <Slider
              className="mt-20"
              value={state.pauseDuration}
              min={3}
              max={30}
              valToString={(val) => `${val}s`}
              onChange={(val) => {
                setState((draft) => {
                  draft.pauseDuration = val;
                });
              }}
            >
              <Slider.Header title="字幕切换时长" />
              <Slider.Bar />
              <Slider.Control />
            </Slider>
            <Tip className="mt-20 text-orange">
              注：当添加多个字幕时，停留该时长后会自动切换下一字幕，当只有一个字幕时则始终显示
            </Tip>
          </>
        )}
      </Collapse>
    );
  };

  const renderActionSetting = () => {
    return (
      <Collapse title="字幕点击操作">
        <Dropdown
          items={actionsItems}
          selected={state.clickAction}
          onChange={(typeId) => {
            setState((draft) => {
              draft.clickAction = typeId;
              if (typeId === SubtitleClickAction.none) {
                draft.clickActionLink = SUBTITLE_DEFAULT.clickActionLink;
                draft.linkTarget = SUBTITLE_DEFAULT.linkTarget;
              }
            });
          }}
        />
        {state.clickAction === SubtitleClickAction.link && (
          <LinkSetting
            className="mt-20"
            value={state.clickActionLink}
            linkTarget={state.linkTarget}
            modalSizeType={state.modalSizeType}
            size={state.size}
            isRequired
            isMusicToggleOn={!!state.turnOffBgMusicOnPlay}
            onChange={(val) =>
              setState((draft) => {
                draft.clickActionLink = val;
              })
            }
            onTargetChange={(val) =>
              setState((draft) => {
                draft.linkTarget = val;
              })
            }
            onMusicToggleChange={(val) =>
              setState((draft) => {
                draft.turnOffBgMusicOnPlay = toFlag(val);
              })
            }
            onModalSizeTypeChange={(k) => {
              setState((draft) => {
                draft.modalSizeType = k;
              });
            }}
            onModalSizeChange={(val) =>
              setState((draft) => {
                draft.size = val;
              })
            }
          />
        )}
      </Collapse>
    );
  };

  return (
    <PanelBase
      isChanged={() => defaultValue !== state}
      title={<Title>{isNew ? '添加字幕' : '编辑字幕'}</Title>}
      onRequestClose={onRequestClose}
      onConfirm={() => {
        settings.current = state;
        onConfirm(index, state);
      }}
      onDeleteClick={() => onDeleteClick(index)}
    >
      {renderContent()}
      {renderSubTitleSetting()}
      {renderPlaySetting()}
      {renderActionSetting()}
    </PanelBase>
  );
}
